import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export default function ForgotPasswordPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/portfolio" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      // Backend always returns 200 to prevent enumeration; surface only
      // genuine network errors here.
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#050505]" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/15693274/pexels-photo-15693274.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Dubai skyline"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="font-serif text-3xl text-white mb-3">Account recovery,<br />the secure way.</h2>
          <p className="text-white/65 text-sm font-sans">
            We'll email you a single-use link valid for 60 minutes — no passwords sent in plain text, ever.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-10">
            <img src="/equia-logo.png" alt="EQUIA Global Property" className="h-20 w-auto" />
          </div>

          {!submitted ? (
            <>
              <h2 className="font-serif text-xl text-white mb-1">Forgot your password?</h2>
              <p className="text-white/60 text-sm mb-8 font-sans">
                Enter your account email and we'll send you a secure link to reset it.
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm mb-6" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-[11px] tracking-[0.15em] uppercase text-white/65 font-sans mb-2 block">Email</label>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none"
                    placeholder="investor@example.com"
                    required
                  />
                </div>

                <button
                  data-testid="forgot-submit-btn"
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#D4AF37] text-black font-medium py-3 text-sm tracking-wide hover:bg-[#B5952F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success" className="border border-[#D4AF37]/20 bg-[#0A0A0A] px-6 py-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-white mb-2">Check your inbox</h3>
                  <p className="text-white/85 text-sm leading-relaxed font-sans">
                    If an account exists for <span className="text-[#D4AF37]">{email}</span>, we've sent a password reset link. It's valid for 60 minutes.
                  </p>
                </div>
              </div>
              <div className="border-t border-white/[0.10] pt-4 mt-4 flex items-center gap-2 text-white/60 text-xs">
                <Mail className="w-3.5 h-3.5" />
                <span>Don't see it? Check spam or request another link in a minute.</span>
              </div>
            </div>
          )}

          <div className="mt-8">
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors font-sans inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
